import { Button } from '@clay-run/glaze/button';
import React, { useCallback, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  ACCOUNT_FIELDS,
  ACCOUNT_PRESETS,
  accountLandingPresetId,
  type AccountRow,
} from '@shared/flow/account-view';
import type { FlowView } from '@shared/flow/view';
import type { FlowIdentity } from '@shared/flow/identity';
import { ACCOUNT_FILTER_FIELDS } from '@shared/flow/filter-fields';
import { TRACKER_GROUP_KEY } from '@shared/flow/tracker';
import { LIST_LAYOUTS, type FlowListLayoutId, type FlowListLayoutSpec } from '@shared/flow/flow-layouts';
import { fmtDate, fmtDateOnly, money, score } from '../cells';
import { formatCompactCount, formatPicks } from '../gs/format';
import { HealthPill, PacePill } from '../gs/GsPill';
import { GsAccountCell } from '../gs/GsAccountCell';
import { applyFlowListLayout, rowEstimateFor } from '../flow-layouts/applyFlowListLayout';
import { useFlowLayoutAwareMachine } from '../flow-layouts/useFlowLayoutAwareMachine';
import { storedWidths, withDefaultWidths } from '../table-core/columns';
import { splitHiddenGroupBy } from '../table-core/grouping';
import type { FlowColumnDef, FlowTableConfig, FlowTableProps } from '../types';
import { CompactUrlLink, DomainLinkCell, LinkedInCell, sfdcRecordUrl } from '../links';
import { useFlowIdentity } from '../useFlowIdentity';
import { useFlowListLayout } from '../../../hooks/useFlowLayouts';
import { withRoleDefaults } from '../roleDefaults';
import { buildMeFilterEmptyState } from '../meEmptyState';
import { useViewStateMachine, type FlowViewMachine, type ViewKind } from '../views/useViewStateMachine';
import { useFlowViewState, useFlowViewStatePersist } from '../views/useViewState';
import { useFlowAccounts } from './hooks';
import { NotesBubble } from '../shell-next/notes/NotesBubble';
import { useNotesSummaries, type NoteSummary } from '../shell-next/notes/useNotesSummaries';
import { useNotesEnabled } from '../../../hooks/useNotesEnabled';

// fmtDate/score/money live in `../cells` — shared with ContactFlowTable (U5
// put the 12 account columns on the contact table, and the same field has to
// read identically on both).

const RENDERERS: Record<string, (row: AccountRow) => React.ReactNode> = {
  name: row => <span className="font-semibold text-content-primary dark:text-nightshade-100">{row.name ?? '—'}</span>,
  propensity_score: row => score(row.propensity_score),
  fit_score: row => score(row.fit_score),
  fortune_500_rank: row => score(row.fortune_500_rank),
  open_pipeline: row => money(row.open_pipeline),
  arr: row => money(row.arr),
  gs_arr: row => money(row.gs_arr),
  arr_opportunity: row => money(row.arr_opportunity),
  pcv: row => money(row.pcv),
  lim_date: row => fmtDate(row.lim_date),
  engagement_score_90_days: row => score(row.engagement_score_90_days),
  l90_signal_count: row => score(row.l90_signal_count),
  l90_website_visits: row => score(row.l90_website_visits),
  l90_active_clay_users: row => score(row.l90_active_clay_users),
  number_of_open_opp: row => score(row.number_of_open_opp),
  number_of_employees: row => score(row.number_of_employees),
  hq: row => {
    const hq = [row.billing_city, row.billing_state].filter(Boolean).join(', ');
    return hq || <span className="text-content-faint dark:text-nightshade-600">—</span>;
  },
  // SFDC domain is a HYPERLINK() formula → the value is an HTML anchor string,
  // not a bare URL. `DomainLinkCell` (links.tsx, QA batch 1 D-F) extracts the
  // href + a clean hostname — shared with the Signals table.
  email_domain_link: row => <DomainLinkCell raw={row.email_domain_link} />,
  // "Signal Link" (lim_link) is a raw Lightning URL — render compact + clickable.
  lim_link: row => <CompactUrlLink url={row.lim_link} label="Signal" />,
  // U6 (Bucket A/E follow-up, 2026-08-27) [ASSUMED DEFAULT — Spencer has NOT
  // confirmed which links/surface despite two asks]: GTME/CDR meeting links,
  // same `CompactUrlLink` treatment as `lim_link` — a null value renders the
  // shared component's empty-cell dash, never a broken anchor.
  owner_meeting_link: row => <CompactUrlLink url={row.owner_meeting_link} label="Meeting" />,
  owner_customer_meeting_link: row => <CompactUrlLink url={row.owner_customer_meeting_link} label="Meeting" />,
  claydr_meeting_link: row => <CompactUrlLink url={row.claydr_meeting_link} label="Meeting" />,
  claydr_customer_meeting_link: row => <CompactUrlLink url={row.claydr_customer_meeting_link} label="Meeting" />,
  // `LinkedInCell` (links.tsx, QA batch 1 D-F): this used to be a raw
  // `href={row.linkedin}` with no `withProtocol` completion — 47,585
  // scheme-less `salesforce_account.linkedin` values on prod-mirror resolved
  // relative to the Terra origin. Now shares the same fix ContactFlowTable's
  // `linkedin` renderer already had.
  linkedin: row => <LinkedInCell url={row.linkedin} />,
  // Growth Strategist ledger fields. One renderer per field for every role, so
  // a non-GS who adds the Health column also sees the pill. `contract_end`,
  // `credit_pace` and `credits_l30` are the heavy fields: every account row
  // carries all three, null when the account has no contract terms or no
  // credit usage in the last 30 days.
  health: row => <HealthPill health={row.health} />,
  // One line by default (standard row height); the tall GS layout stacks it (registry cell variant `pace-stacked`, see CELLS below).
  credit_pace: row => <PacePill ratio={row.credit_pace} layout="horizontal" />,
  credits_l30: row => <span className="tabular-nums">{formatCompactCount(row.credits_l30)}</span>,
  contract_end: row => fmtDateOnly(row.contract_end),
  renewal_date: row => fmtDateOnly(row.renewal_date),
  next_gs_assign_date: row => fmtDateOnly(row.next_gs_assign_date),
  // The three meeting dates are timestamptz columns (an instant, so `fmtDate`);
  // the three above are DATE columns (a calendar day, so `fmtDateOnly`).
  last_gs_meeting: row => fmtDate(row.last_gs_meeting),
  last_claydr_meeting: row => fmtDate(row.last_claydr_meeting),
  last_gtme_meeting: row => fmtDate(row.last_gtme_meeting),
  red_reason: row => formatPicks(row.red_reason),
  risk_reasons: row => formatPicks(row.risk_reasons),
  sfdc_link: row => (
    <a
      href={sfdcRecordUrl('Account', row.id)}
      target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      className="text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300"
    >
      <ExternalLink className="w-3.5 h-3.5 inline" />
    </a>
  ),
};

const NUMERIC_KEYS = new Set([
  'propensity_score', 'fit_score', 'open_pipeline', 'l90_signal_count',
  'engagement_score_90_days', 'number_of_open_opp', 'number_of_employees',
  'arr', 'gs_arr', 'arr_opportunity', 'pcv', 'l90_website_visits', 'l90_active_clay_users',
  'number_of_workspace', 'workspaces_created_last_90_days', 'fortune_500_rank',
  'credits_l30',
]);

const ACCOUNT_COLUMNS: FlowColumnDef<AccountRow>[] = ACCOUNT_FIELDS.map(f => ({
  key: f.key,
  label: f.label,
  type: f.type,
  noFilter: f.noFilter,
  render: RENDERERS[f.key],
  numeric: NUMERIC_KEYS.has(f.key),
}));

/**
 * Everything the table draws differently per `companies` list layout, in one
 * object. The hook reads fields from it, so no other line asks which layout
 * (or role) it is serving.
 */
interface AccountTableSettings {
  columns: FlowColumnDef<AccountRow>[];
  // The GS layout has no Status column, so `tracker` is left out of its
  // config. That also drops the Tracker Status group-by option.
  tracker: FlowTableConfig<AccountRow>['tracker'];
  // The GS layout has no `#` column, and its two-line Account cell starts rows
  // from the taller estimate. V1 ignores both. CDR carries its own values
  // explicitly (`#` on, the dense estimate), which LedgerTable treats as absent.
  rowChrome: Pick<FlowTableConfig<AccountRow>, 'showRowNumbers' | 'rowEstimatePx'>;
  defaultWidths: Readonly<Record<string, number>>;
  hiddenGroupBy: readonly string[];
}

/** The cell renderer behind each registry cell variant (`spec.cells.companies`). */
const CELLS = {
  'gs-account': (row: AccountRow) => <GsAccountCell row={row} />,
  'pace-stacked': (row: AccountRow) => <PacePill ratio={row.credit_pace} layout="stacked" />,
};

/**
 * The table's settings for a list layout. Built once per layout at module
 * scope so `config`, `columnWidths` and every row keep stable references
 * across renders.
 */
function accountTableSettingsFor(spec: FlowListLayoutSpec): AccountTableSettings {
  return {
    columns: applyFlowListLayout(ACCOUNT_COLUMNS, spec, { table: 'companies', cells: CELLS }),
    tracker: spec.showTracker ? { entity: 'account' } : undefined,
    rowChrome: { showRowNumbers: spec.showRowNumbers, rowEstimatePx: rowEstimateFor(spec, 'companies') },
    defaultWidths: spec.defaultWidths.companies ?? {},
    hiddenGroupBy: spec.showTracker ? [] : [TRACKER_GROUP_KEY],
  };
}

const SETTINGS_BY_LAYOUT = {
  cdr: accountTableSettingsFor(LIST_LAYOUTS.cdr),
  gs: accountTableSettingsFor(LIST_LAYOUTS.gs),
} satisfies Record<'cdr' | 'gs', AccountTableSettings>;

export interface UseAccountTableModelParams {
  /** Row click → drill-down navigation (owned by the mini-app shell). V1 `AccountFlowTable` only. */
  onAccountClick?: (salesforceAccountId: string) => void;
  /**
   * V2 `CompaniesTable` only (plan 2026-09-28-001, clay 3): builds the
   * per-row drill-down URL for `config.rowHref`, so `LedgerTable` can render
   * a real, focusable react-router link per row instead of a plain click
   * handler. `AccountFlowTable` (V1) never passes this, so its `config`
   * keeps `rowHref: undefined` — behaviour-identical.
   */
  accountHref?: (salesforceAccountId: string) => string;
}

export interface UseAccountTableModelResult {
  machine: FlowViewMachine;
  identity: FlowIdentity | null;
  tableProps: FlowTableProps<AccountRow>;
  /** Batch Notes summaries for the loaded pages; the table provides it to row bubbles via `NotesSummariesContext`. */
  notesSummaries: ReadonlyMap<string, NoteSummary>;
}

/**
 * Headless account-table model (plan 2026-09-27-007 U2a, D1) — every hook
 * `AccountFlowTable` used to run inline, unchanged in order or behaviour, so
 * a V2 mount can drive the same real data/filter/grouping/views machinery
 * without forking it. `AccountFlowTable` itself is now a thin renderer over
 * this hook's `tableProps`.
 *
 * MUST be called inside a react-router context (U6): the saved-views state
 * machine reads/writes the shareable `?view=<id>` param through
 * `useSearchParams`.
 */
export function useAccountTableModel({ onAccountClick, accountHref }: UseAccountTableModelParams): UseAccountTableModelResult {
  const identityQuery = useFlowIdentity();
  const identityReady = identityQuery.isSuccess || identityQuery.isError;
  const identity = identityQuery.data ?? null;
  const flowRole = identity?.flowRole;
  // Role-based landing default (roleDefaults.ts): each rep lands on their own
  // book by role — ClayDR gets claydr ownership + the not-agency/no-open-opp
  // chips, GTME gets owner, GS gets growth strategist, admins/unrecognized
  // roles get no default (show all). Fully clearable.
  //
  // Seeded on every shared preset and draft open, never on the two Growth
  // Strategist presets (offered to every rep, like any other preset). MERGED alongside the view's own filter, so presets
  // that carry their own conditions (By Last Interesting Moment) still stay
  // bounded to the rep's book. `withRoleDefaults`
  // (roleDefaults.ts) is the one merge implementation — ContactFlowTable
  // calls the same function.
  //
  // A SAVED view is never adapted: it owns its complete predicate (doc), so
  // opening a teammate's view shows exactly what they saved.
  //
  // Because the adapted view is also the dirty BASELINE, a rep who has done
  // nothing but land on the page never sees a "Modified" banner — and
  // clearing a seeded chip correctly reads as a modification.
  const adaptOnOpen = useCallback((view: FlowView, kind: ViewKind): FlowView => {
    if (kind === 'saved') return view;
    return withRoleDefaults(view, identity, 'account');
  }, [identity]);

  // Server-side working-state store (U9 / R8), per `(user, 'account')` — a
  // SEPARATE row from the contacts table's: the two entities have different
  // field catalogs, so their states are never interchangeable.
  const viewState = useFlowViewState('account');

  // Saved views + presets + dirty tracking + draft lifecycle (U6). Replaces
  // the old `switchView` local-state reset.
  //
  // `defaultPresetId` is the LANDING preset (QA-2 / V6): a rep who has never
  // set a home view (no `?view=`, no stored active/default pointer) lands on
  // Tracker, not "By Propensity". A Growth Strategist lands on GS My Book
  // instead (`accountLandingPresetId`). Deliberately NOT the same thing as
  // `ACCOUNT_DEFAULT_PRESET.id` / `isDefault` — that flag means "this mount's
  // canonical baseline" and stays put, because nothing else here keys off
  // "which preset is the landing one" (unlike ContactFlowTable's account
  // mount, this table has no isDefault-gated ranking/sort override to leak
  // into). See `ACCOUNT_LANDING_PRESET_ID`'s doc comment in
  // `@shared/flow/account-view`.
  const rawMachine = useViewStateMachine({
    entity: 'account',
    presets: ACCOUNT_PRESETS,
    defaultPresetId: accountLandingPresetId(flowRole),
    // Identity arrives async (useFlowIdentity) and so does the stored view
    // state; gating the first resolution on BOTH means the accounts fetch
    // never fires with unseeded filters — no unfiltered-then-filtered flash,
    // and no restore landing after the fetch already went out. The layout is a
    // separate gate (`layout.isLoaded`): the machine's defaults follow the
    // identity, not the UI View, so only the fetch below waits for it too.
    ready: identityReady && viewState.isRestored,
    adaptOnOpen,
    viewState,
  });

  // The `companies` list layout decides what the table draws (labels, name
  // cell, alignment, `#`, density, default widths, tracker). It follows the
  // UI View by default; the open view's pin, the rep's saved choice and an
  // unsaved pick from the Layout menu beat it, in that order upward. The
  // landing preset and the role defaults above follow the real team, through
  // the identity. The wrapped machine reads an unsaved pick as "Modified",
  // drops it on Reset / view switch and commits it on Save / Create view —
  // every later line (and the caller) uses that machine.
  const layout = useFlowListLayout('companies', {
    viewPin: rawMachine.activeView.flowLayout ?? null,
  });
  const machine = useFlowLayoutAwareMachine(rawMachine, 'companies', layout);
  // 'signal-response' applies to Signals only and `available` filters it out for companies, so the cdr fallback is unreachable.
  const settingsById: Partial<Record<FlowListLayoutId, AccountTableSettings>> = SETTINGS_BY_LAYOUT;
  const settings = settingsById[layout.id] ?? SETTINGS_BY_LAYOUT.cdr;
  // The layout reads its UI View from /api/me and the view preferences, a
  // different pair of requests from the identity and the stored view state,
  // so the first fetch also waits for it.
  const layoutLoaded = layout.isLoaded;
  const { enabled: notesEnabled } = useNotesEnabled();

  const config: FlowTableConfig<AccountRow> = useMemo(() => ({
    columns: settings.columns,
    // Filterable fields are DECOUPLED from the visible columns (R5) — the
    // catalog carries `contact_*` EXISTS fields this table never shows.
    filterFields: ACCOUNT_FILTER_FIELDS,
    rowKey: row => row.id,
    onRowClick: onAccountClick ? (row => onAccountClick(row.id)) : undefined,
    // V2 `LedgerTable` only (clay 3) — `undefined` unless the caller passes
    // `accountHref` (V1's `AccountFlowTable`/`FlowTable` never does, and
    // ignores `rowHref` even when set).
    rowHref: accountHref ? (row => accountHref(row.id)) : undefined,
    // No renderDetail (rows navigate) and no Actions column (accounts don't
    // enroll). The Notes bubble is the leading column (`rowLead`), ONLY while
    // Notes is on — `undefined` otherwise so no empty column appears. The
    // bubble prevents default + stops propagation, so the row `<Link>` holds.
    rowLead: notesEnabled
      ? (row: AccountRow) => (
          <NotesBubble
            target={{ kind: 'account', id: row.id, accountId: row.id }}
            mirrorInput={row.account_notes ?? undefined}
          />
        )
      : undefined,
    // Always a global CC mount — the omnibox handles search (D7).
    showSearch: false,
    // Tracker row chip (U7 / R6) — status lives on `row.tracker_status` etc.,
    // carried by the same /api/flow/accounts response (LEFT JOIN, no extra fetch).
    tracker: settings.tracker,
    ...settings.rowChrome,
  }), [onAccountClick, accountHref, settings, notesEnabled]);

  const { filter: filters, groupLogic, sort, groupBy, columns: visibleColumns, columnWidths: workingWidths } = machine.working;

  // The GS layout draws the ledger's default widths under whatever the user
  // has dragged. Only the dragged widths reach the working state: the table
  // hands back every width it drew, so `storedWidths` drops the defaults the
  // user never touched. The CDR layout has no defaults, so it draws and stores
  // exactly the working state's widths.
  const columnWidths = useMemo(() => withDefaultWidths(settings.defaultWidths, workingWidths), [settings, workingWidths]);
  const { setColumnWidths } = machine;
  const onColumnWidthsChange = useCallback(
    (next: Record<string, number>) => setColumnWidths(storedWidths(settings.defaultWidths, next, workingWidths)),
    [settings, setColumnWidths, workingWidths],
  );

  // The GS layout draws no tracker Status column and offers no Tracker Status grouping,
  // so the Tracker landing preset's grouping would head the rows with a group
  // the Group menu cannot explain. `working` keeps the grouping, so the
  // persisted view and a switch of the layout back both still carry it.
  const { setGroupBy } = machine;
  const hidden = settings.hiddenGroupBy;
  const { shown: effectiveGroupBy, maxLevels: maxGroupLevels, restore } = useMemo(
    () => splitHiddenGroupBy(groupBy, hidden),
    [groupBy, hidden],
  );
  const onGroupByChange = useCallback((next: string[]) => setGroupBy(restore(next)), [setGroupBy, restore]);

  // Write-back half: every dirty-relevant change, debounced ~1s.
  useFlowViewStatePersist({
    stateKey: 'account',
    enabled: machine.isReady,
    activeViewId: machine.activeViewId,
    working: machine.working,
  });

  // Search is deliberately NOT part of the view's working state — typing here
  // never dirties a view (doc's dirty-state table).
  const [search, setSearch] = useState('');

  const query = useFlowAccounts({
    search,
    filters,
    groupLogic,
    sort,
    groupBy: effectiveGroupBy,
    enabled: machine.isReady && layoutLoaded,
  });

  const rows = useMemo(
    () => (query.data?.pages ?? []).flatMap(p => p.rows),
    [query.data],
  );

  const notesPages = useMemo(
    () => (query.data?.pages ?? []).map(page => page.rows.map(row => ({ kind: 'account' as const, id: row.id }))),
    [query.data],
  );
  const notesSummaries = useNotesSummaries(notesPages);

  // NOTE: the #1091 empty-book "Browse all accounts" CTA was removed here —
  // Command Center rankings are a cross-book prospecting surface (product
  // decision 2026-07-23), so the my-book-is-empty state is unreachable for
  // accounts. Since U5 that is true by construction rather than by preset
  // default: there is no `scope` param at all, and ContactFlowTable's twin CTA
  // is gone for the same reason. The affordance for a zero-match table is
  // CLEAR FILTERS — adjusted filters with zero matches previously showed a
  // dead-end "No results." (Spencer, 2026-07-24).
  //
  // A `{me}` filter takes precedence: it names the specific cause and clears
  // only that chip, where "Clear filters" would silently wipe the rep's other
  // conditions too (shared with ContactFlowTable — see meEmptyState.tsx).
  const baselineFilters = machine.baseline.filter;
  const filtersAreBaseline = JSON.stringify(filters) === JSON.stringify(baselineFilters);
  const emptyState =
    buildMeFilterEmptyState({
      filters,
      filterFields: ACCOUNT_FILTER_FIELDS,
      identity,
      onFiltersChange: machine.setFilters,
    })
    ?? (!filtersAreBaseline ? (
      <span>
        No accounts match the current filters.{' '}
        <Button variant="inline-text" size="xs" onClick={() => machine.setFilters(baselineFilters)}>
          Clear filters
        </Button>
      </span>
    ) : baselineFilters.length > 0 ? (
      <span>
        No accounts match this view.{' '}
        <Button variant="inline-text" size="xs" onClick={() => machine.reset()}>
          Reset view
        </Button>
      </span>
    ) : undefined);

  const tableProps: FlowTableProps<AccountRow> = {
    views: ACCOUNT_PRESETS,
    viewMachine: machine,
    config,
    emptyState,
    rows,
    isLoading: !machine.isReady || !layoutLoaded || query.isLoading,
    isRefetching: query.isPlaceholderData || (query.isFetching && !query.isFetchingNextPage && !query.isLoading),
    error: query.error ? query.error.message : null,
    onRetry: () => query.refetch(),
    hasMore: !!query.hasNextPage,
    onLoadMore: () => query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
    activeViewId: machine.activeViewId,
    onViewChange: machine.openView,
    sort,
    onSortChange: machine.setSort,
    filters,
    onFiltersChange: machine.setFilters,
    groupLogic,
    onGroupLogicChange: machine.setGroupLogic,
    search,
    onSearchChange: setSearch,
    groupBy: effectiveGroupBy,
    maxGroupLevels,
    onGroupByChange,
    visibleColumns,
    onVisibleColumnsChange: machine.setColumns,
    // U10 — drag-resizable widths (Spencer 2026-08-27: both tables). Pure
    // wiring; the capability lives in FlowTable. This mount owns the
    // `'account'` state row and does NOT set `skipWorkingRestore`, so
    // account widths restore across remounts like the rest of its working
    // state.
    columnWidths,
    onColumnWidthsChange,
    totalLoaded: rows.length,
    identity,
  };

  return { machine, identity, tableProps, notesSummaries };
}
